import { AsyncPipe, DOCUMENT, isPlatformBrowser, ViewportScroller } from '@angular/common';
import {
    afterNextRender,
    Component,
    HostListener,
    inject,
    Injector,
    OnDestroy,
    OnInit,
    PLATFORM_ID,
    Renderer2,
    RendererStyleFlags2,
    ChangeDetectionStrategy,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, Scroll } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ICustomWindow, PAGE_SERVICE, SectionComponent, WindowRefService } from '@sirius/common';
import { setCollectorUrl } from '@snowplow/browser-tracker';
import { UserCommerce } from 'commerce/models';
import { UserService } from 'commerce/services';
import { filter, finalize, Subscription } from 'rxjs';
import { LoadingSpinnerComponent, LoadingSpinnerService } from 'shared';
import { environment } from '../../../environments';
import { StoryblokEditableDirective } from '../../directives';
import { Page } from '../../models';
import {
    CustomCookieService,
    HeadService,
    LeadooService,
    OptimizelyService,
    PageDefaultService,
    StoryblokBridgeService,
} from '../../services';
import { EditorModeComponent } from '../editor-mode/editor-mode.component';
import { ErrorComponent } from '../error/error.component';
import { FooterWrapperComponent } from '../wrappers/footer-wrapper/footer-wrapper.component';
import { NavigationWrapperComponent } from '../wrappers/navigation-wrapper/navigation-wrapper.component';
import { UtilityBarWrapperComponent } from '../wrappers/utility-bar-wrapper/utility-bar-wrapper.component';
import { PageTrackingService } from './page.tracking';
@Component({
    selector: 'butterfly-page',
    templateUrl: './page.component.html',
    styleUrls: ['./page.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        EditorModeComponent,
        ErrorComponent,
        UtilityBarWrapperComponent,
        NavigationWrapperComponent,
        SectionComponent,
        FooterWrapperComponent,
        LoadingSpinnerComponent,
        AsyncPipe,
        TranslatePipe,
        StoryblokEditableDirective,
    ],
})
export class PageComponent implements OnInit, OnDestroy {
    pageService = inject<PageDefaultService>(PAGE_SERVICE);
    private _router = inject(Router);
    private _activatedRoute = inject(ActivatedRoute);
    private _translateService = inject(TranslateService);
    private _renderer = inject(Renderer2);
    private _headService = inject(HeadService);
    private _optimizelyService = inject(OptimizelyService);
    private _viewportScroller = inject(ViewportScroller);
    private _userService = inject(UserService);
    private _windowRef = inject(WindowRefService);
    private _leadooService = inject(LeadooService);
    private _pageTracking = inject(PageTrackingService);
    private _customCookieService = inject(CustomCookieService);
    private _storyblokBridge = inject(StoryblokBridgeService);
    private _injector = inject(Injector);
    private _document = inject(DOCUMENT);
    private _isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

    page?: Page;
    loadingSpinnerService = inject(LoadingSpinnerService);

    private _navSubscription?: Subscription;
    private _history: string[] = [];
    private _loadedPath?: string;
    private _awaitingPage = false;
    private _pendingScroll?: Scroll;
    /**
     * `pageChange` fires both for the initial bootstrap page and for every
     * subsequent client-side navigation. We only want to re-focus the
     * `<main>` landmark on the *subsequent* ones — stealing focus on the
     * very first render would violate WCAG 2.4.3 (Focus Order) by pulling
     * the user away from wherever they intentionally landed.
     */
    private _isFirstPageLoad = true;

    private get Window(): ICustomWindow | undefined {
        return this._windowRef.NativeWindow;
    }

    @HostListener('copy')
    onCopy(): void {
        this._pageTracking.trackTextCopiedGtm();
    }

    @HostListener('click', ['$event'])
    onClick(event: MouseEvent): void {
        this._pageTracking.trackRawLinkClicked(event.target);
    }

    ngOnInit(): void {
        this.pageService.pageChange.subscribe((x: Page) => {
            if (x.culture === 'zh-Hans') {
                // Skip on the server: SSR has no Window and `main.server.ts`
                // does not bootstrap the Snowplow tracker, so calling
                // `setCollectorUrl` there would be a no-op against an
                // uninitialized tracker.
                if (!this.Window) {
                    return;
                }
                setCollectorUrl(environment.snowplow.zhHansEndpoint, ['corporate-tracker']);
            }

            this.reloadPage(x);
        });

        this._navSubscription = this._router.events
            .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                this.onNavigation(event);
            });

        if (!this.Window) {
            return;
        }

        this._viewportScroller.setHistoryScrollRestoration('manual');
        this._navSubscription.add(
            this._router.events
                .pipe(filter((event): event is Scroll => event instanceof Scroll))
                .subscribe((event: Scroll) => this.onScroll(event))
        );
        this._history = [location.href];

        this._renderer.listen(window, 'CookiebotOnConsentReady', () => {
            if (this.Window && this.Window.Cookiebot.consent.statistics) {
                this._customCookieService.setCookies();
            }
        });

        this._leadooService.enableTracking();
    }

    ngOnDestroy(): void {
        this._navSubscription?.unsubscribe();
    }

    private reloadPage(page: Page) {
        if (this._isBrowser && !this._isFirstPageLoad) {
            this.suspendScrollAnchoring();
        }

        this.page = page;
        this.setUser(page);
        this._translateService.use(page.culture);
        this._headService.init(
            this._renderer,
            page.content.header,
            page.culture,
            page.content.body.isInEditor,
            page.content.structuredDataScript,
        );
        this._pageTracking.trackPage(this.page, this._history, this._activatedRoute);

        // Must stay above the `_isFirstPageLoad` early return below: inside the
        // Storyblok Visual Editor's iframe the initial load is usually the only
        // load, so bailing out early would never start the bridge.
        if (page.content.body.isInEditor) {
            afterNextRender(
                () =>
                    void this._storyblokBridge
                        .init()
                        .catch((error: unknown) => console.error('Storyblok preview bridge failed to load', error)),
                { injector: this._injector }
            );
        } else {
            // The Optimizely snippet is excluded in the preview (`excludeInStoryblokPreview`).
            this._optimizelyService.activate();
        }

        this._awaitingPage = false;
        afterNextRender(() => this.flushPendingScroll(), { injector: this._injector });

        if (this._isFirstPageLoad) {
            this._isFirstPageLoad = false;
            return;
        }

        // Reseat focus on the new page's `<main>` once Angular has rendered
        // the swapped template. `afterNextRender` runs only in the browser
        // (no-op on SSR) and waits for the post-CD DOM commit, so the new
        // `<main>` element is guaranteed to exist when the callback fires.
        afterNextRender(() => this.focusMainContent(), { injector: this._injector });
    }

    /**
     * Every section is rebuilt from the new page model. The browser's scroll
     * anchor sits in the content being replaced, so anchoring would move the
     * page, e.g. to the bottom after paging an overview. `applyScroll` and the
     * component that requested the update own the position instead.
     */
    private suspendScrollAnchoring(): void {
        const root = this._document.documentElement;
        this._renderer.setStyle(root, 'overflow-anchor', 'none', RendererStyleFlags2.DashCase);
        afterNextRender(
            { write: () => this._renderer.removeStyle(root, 'overflow-anchor', RendererStyleFlags2.DashCase) },
            { injector: this._injector }
        );
    }

    /**
     * Move focus to the page's primary content landmark. Called after every
     * non-initial client-side navigation so:
     *
     *   1. The browser's sequential focus navigation starting point is reset
     *      to the top of the new page (otherwise it remains at the position
     *      of the now-removed link the user clicked, and Tab continues from
     *      there — typically landing in the footer).
     *   2. Screen readers announce the new page heading on the next
     *      utterance, instead of staying silent because the active element
     *      hasn't logically changed.
     *
     * `preventScroll: true` leaves the scroll position to `applyScroll`.
     */
    private focusMainContent(): void {
        const main = this._document.getElementById('main-content');
        if (!main) {
            return;
        }
        main.focus({ preventScroll: true });
    }

    /**
     * Handle the "Skip to main content" link. Bypasses the browser's native
     * `href="#main-content"` follow-through so we don't:
     *   - Scroll the page even though `<main>` is already at the top of
     *     the content area.
     *   - Push a `#main-content` hash entry into `history`, which the user
     *     would otherwise have to back out of before they can leave the page.
     *
     * The DOM `<a href="#main-content">` is kept so the anchor is statically
     * focusable and the link still degrades gracefully without JavaScript.
     */
    protected onSkipToMain(event: Event): void {
        event.preventDefault();
        this.focusMainContent();
    }

    private onNavigation(event: NavigationEnd) {
        const path = event.urlAfterRedirects.split('#')[0];
        if (path === this._loadedPath) {
            return;
        }
        this._loadedPath = path;
        this._awaitingPage = true;

        this.pageService
            .getPage(event.urlAfterRedirects)
            .pipe(finalize(() => (this._awaitingPage = false)))
            .subscribe();

        if (!this.Window) {
            return;
        }

        this.updateHistory(event.urlAfterRedirects);
    }

    /** Page content arrives after `NavigationEnd`, so scrolling waits until it has rendered. */
    private onScroll(scroll: Scroll) {
        if (this._awaitingPage) {
            this._pendingScroll = scroll;
            return;
        }

        this.applyScroll(scroll);
    }

    private flushPendingScroll() {
        if (!this._pendingScroll) {
            return;
        }

        this.applyScroll(this._pendingScroll);
        this._pendingScroll = undefined;
    }

    private applyScroll({ position, anchor }: Scroll) {
        if (position) {
            this._viewportScroller.scrollToPosition(position, { behavior: 'instant' });
        } else if (anchor) {
            this._document.getElementById(anchor)?.scrollIntoView();
        } else {
            this._viewportScroller.scrollToPosition([0, 0]);
        }
    }

    private setUser(page: Page) {
        if (!page.user) {
            return;
        }

        const user: UserCommerce = {
            name: page.user.name,
            userName: page.user.userName,
            hasMarketplaceAccess: Boolean(page.user.hasMarketplaceAccess),
            companyName: page.user.companyName,
        };

        this._userService.setUser(user);
    }

    private updateHistory(urlAfterRedirects: string) {
        const curUrl = location.origin + urlAfterRedirects;
        this._history = [...this._history, curUrl];
    }
}
